<script lang="ts">
    import { onMount, onDestroy } from 'svelte';
    import { link } from 'svelte-spa-router';
    import { api } from '../framework/js/api.js';
    import { toast } from '../framework/js/toast.js';

    interface AiJob {
        uuid: string;
        user_uuid: string;
        user: { uuid: string; first_name: string; last_name: string; email: string } | null;
        subject_type: string;
        subject_uuid: string;
        subject_name?: string | null;
        subject_path?: string;
        status: string;
        error_message: string | null;
        duration_seconds: number | null;
        started_at: string | null;
        finished_at: string | null;
        created_at: string;
    }

    const STATUS_LABELS: Record<string, string> = {
        'queued': 'Queued',
        'processing': 'Processing',
        'completed': 'Completed',
        'failed': 'Failed'
    };

    const STATUS_COLORS: Record<string, string> = {
        'queued': 'var(--status-warn)',
        'processing': 'var(--status-info)',
        'completed': 'var(--status-success)',
        'failed': 'var(--status-danger)'
    };

    const SUBJECT_LABELS: Record<string, string> = {
        'brief': 'Built-to-Brief',
        'assessment': 'Illuminator',
        'gapfinder': 'GapFinder'
    };

    const SUBJECT_ICONS: Record<string, string> = {
        'brief': 'pencil alternate',
        'assessment': 'lightbulb',
        'gapfinder': 'search'
    };

    const STATUS_OPTIONS = [
        { value: '', label: 'All Statuses' },
        { value: 'queued', label: 'Queued' },
        { value: 'processing', label: 'Processing' },
        { value: 'completed', label: 'Completed' },
        { value: 'failed', label: 'Failed' }
    ];

    const SUBJECT_OPTIONS = [
        { value: '', label: 'All Types' },
        { value: 'brief', label: 'Built-to-Brief' },
        { value: 'assessment', label: 'Illuminator' },
        { value: 'gapfinder', label: 'GapFinder' }
    ];

    let items: AiJob[] = [];
    let isLoading = false;
    let isInitialized = false;
    let statusFilter = '';
    let subjectTypeFilter = '';
    let paging = { current_page: 0, last_page: 1, per_page: 20, total: 0, from: 0, to: 0 };
    let currentPage = 1;
    let selectedJob: AiJob | null = null;
    let showDetailModal = false;
    let autoRefreshTimer: ReturnType<typeof setInterval> | null = null;

    onMount(async () => {
        try {
            await loadJobs();
        } finally {
            isInitialized = true;
        }
        autoRefreshTimer = setInterval(() => {
            const hasActive = items.some(j => j.status === 'queued' || j.status === 'processing');
            if (hasActive) loadJobs(true);
        }, 10000);
    });

    onDestroy(() => {
        if (autoRefreshTimer) clearInterval(autoRefreshTimer);
    });

    async function loadJobs(silent = false) {
        if (!silent) isLoading = true;
        try {
            const params: Record<string, string | number> = {
                page: currentPage,
                per_page: 20
            };
            if (statusFilter) params.status = statusFilter;
            if (subjectTypeFilter) params.subject_type = subjectTypeFilter;

            const response: any = await api.getAiJobs(params);
            if (response.success) {
                items = response.items || [];
                paging = response.paging || paging;
            } else {
                if (!silent) toast.showNotification(false, response.message || 'Failed to load AI jobs');
                items = [];
            }
        } catch (error) {
            if (!silent) toast.showNotification(false, 'Error loading AI jobs');
            console.error(error);
            items = [];
        } finally {
            isLoading = false;
        }
    }

    function applyFilters() {
        currentPage = 1;
        loadJobs();
    }

    function clearFilters() {
        statusFilter = '';
        subjectTypeFilter = '';
        currentPage = 1;
        loadJobs();
    }

    function formatDate(dateString: string | null): string {
        if (!dateString) return '—';
        return new Date(dateString).toLocaleString(navigator.language, {
            dateStyle: 'medium',
            timeStyle: 'short'
        });
    }

    function formatDuration(seconds: number | null): string {
        if (seconds === null || seconds === undefined) return '—';
        if (seconds < 60) return `${seconds}s`;
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}m ${secs}s`;
    }

    function getStatusLabel(status: string): string {
        return STATUS_LABELS[status] || status;
    }

    function getStatusColor(status: string): string {
        return STATUS_COLORS[status] || 'var(--grey-secondary)';
    }

    function getSubjectLabel(subjectType: string): string {
        return SUBJECT_LABELS[subjectType] || subjectType;
    }

    function getSubjectIcon(subjectType: string): string {
        return SUBJECT_ICONS[subjectType] || 'cog';
    }

    function getItemDisplayName(job: AiJob): string {
        const n = job.subject_name?.trim();
        if (n) return n;
        return `Open ${getSubjectLabel(job.subject_type)}`;
    }

    function openDetail(job: AiJob) {
        selectedJob = job;
        showDetailModal = true;
    }

    function closeDetailModal() {
        showDetailModal = false;
        selectedJob = null;
    }

    function handleKeydown(event: KeyboardEvent) {
        if (event.key === 'Escape') closeDetailModal();
    }

    function goToPage(page: number) {
        currentPage = page;
        loadJobs();
    }

    $: hasFilters = statusFilter || subjectTypeFilter;
    $: activeCount = items.filter(j => j.status === 'queued' || j.status === 'processing').length;
</script>

<svelte:window on:keydown={handleKeydown} />

<div class="ai-jobs-page">
    <div class="page-header">
        <div class="header-content">
            <h2 class="page-title">AI Processes</h2>
            <p class="page-subtitle">Background AI generation jobs for your team</p>
        </div>
        {#if activeCount > 0}
            <div class="active-indicator">
                <span class="pulse-dot-header"></span>
                <span>{activeCount} active</span>
            </div>
        {/if}
    </div>

    {#if !isInitialized}
        <div class="loading-state" aria-live="polite" aria-busy="true">
            <div class="loading-spinner" aria-hidden="true">
                <i class="spinner loading icon"></i>
            </div>
            <p class="loading-text">Loading AI processes…</p>
        </div>
    {:else}
        <!-- Filters -->
        <div class="search-filter-bar">
            <div class="filter-controls">
                <select id="status-filter" bind:value={statusFilter} on:change={applyFilters} class="filter-select" aria-label="Filter by status">
                    {#each STATUS_OPTIONS as option}
                        <option value={option.value}>{option.label}</option>
                    {/each}
                </select>
                <select id="type-filter" bind:value={subjectTypeFilter} on:change={applyFilters} class="filter-select" aria-label="Filter by type">
                    {#each SUBJECT_OPTIONS as option}
                        <option value={option.value}>{option.label}</option>
                    {/each}
                </select>
                {#if hasFilters}
                    <button type="button" class="clear-filters-btn" on:click={clearFilters}>
                        <i class="times icon"></i> Clear
                    </button>
                {/if}
                <button type="button" class="refresh-btn" on:click={() => loadJobs()} disabled={isLoading} title="Refresh">
                    <i class="refresh icon"></i>
                </button>
            </div>
            <div class="results-count">
                {paging.total} {paging.total === 1 ? 'process' : 'processes'}
            </div>
        </div>

        {#if items.length === 0 && hasFilters}
            <div class="empty-state">
                <div class="empty-icon-wrap">
                    <i class="cogs icon"></i>
                </div>
                <p class="empty-title">No AI processes found</p>
                <p class="empty-hint">No processes match the current filters.</p>
                <button type="button" class="clear-filters-btn" on:click={clearFilters}>Clear filters</button>
            </div>
        {:else if items.length === 0}
            <div class="empty-zero-state">
                <div class="zero-icon-wrap">
                    <i class="cogs icon"></i>
                </div>
                <h3 class="zero-title">No AI processes yet</h3>
                <p class="zero-hint">When you or your teammates run an AI agent, the background job will show up here so you can track its progress.</p>
                <div class="zero-actions">
                    <a class="zero-action-card" href="/briefs">
                        <div class="zero-action-icon zero-action-icon--brief" aria-hidden="true"><i class="file text icon"></i></div>
                        <div class="zero-action-text">
                            <span class="zero-action-title">Built-to-Brief</span>
                            <span class="zero-action-sub">Generate a brief</span>
                        </div>
                    </a>
                    <a class="zero-action-card" href="/gapfinder">
                        <div class="zero-action-icon zero-action-icon--gapfinder" aria-hidden="true"><i class="shield alternate icon"></i></div>
                        <div class="zero-action-text">
                            <span class="zero-action-title">GapFinder</span>
                            <span class="zero-action-sub">Run a gap analysis</span>
                        </div>
                    </a>
                </div>
            </div>
        {:else}
            <div class="jobs-table-wrapper">
                <table class="jobs-table">
                    <thead>
                        <tr>
                            <th class="th-type"><i class="cog icon"></i> Agent</th>
                            <th class="th-item"><i class="file outline icon"></i> Report</th>
                            <th class="th-status"><i class="signal icon"></i> Status</th>
                            <th class="th-user"><i class="user icon"></i> User</th>
                            <th class="th-created"><i class="calendar plus icon"></i> Created</th>
                            <th class="th-duration"><i class="clock icon"></i> Duration</th>
                        </tr>
                    </thead>
                    <tbody>
                        {#each items as job (job.uuid)}
                            <tr class="job-row" on:click={() => openDetail(job)} role="button" tabindex="0" on:keydown={(e) => e.key === 'Enter' && openDetail(job)}>
                                <td class="td-type">
                                    <div class="type-wrapper">
                                        <div class="type-icon" data-subject={job.subject_type}>
                                            <i class="{getSubjectIcon(job.subject_type)} icon"></i>
                                        </div>
                                        <span class="type-label">{getSubjectLabel(job.subject_type)}</span>
                                    </div>
                                </td>
                                <td class="td-item">
                                    {#if job.subject_path}
                                        <a
                                            use:link
                                            href={job.subject_path}
                                            class="item-link"
                                            title={getItemDisplayName(job)}
                                            on:click|stopPropagation
                                        >
                                            <span class="item-link-text">{getItemDisplayName(job)}</span>
                                            <i class="chevron right icon item-link-icon" aria-hidden="true"></i>
                                        </a>
                                    {:else}
                                        <span class="text-muted">—</span>
                                    {/if}
                                </td>
                                <td class="td-status">
                                    <span class="status-badge" style="--badge-color: {getStatusColor(job.status)}">
                                        {#if job.status === 'processing'}
                                            <span class="pulse-dot"></span>
                                        {/if}
                                        {getStatusLabel(job.status)}
                                    </span>
                                </td>
                                <td class="td-user">
                                    {#if job.user}
                                        <span class="user-name">{job.user.first_name} {job.user.last_name}</span>
                                    {:else}
                                        <span class="text-muted">—</span>
                                    {/if}
                                </td>
                                <td class="td-created">
                                    <span class="date-value">{formatDate(job.created_at)}</span>
                                </td>
                                <td class="td-duration">
                                    {#if job.duration_seconds !== null && job.duration_seconds !== undefined}
                                        <div class="duration-badge">
                                            <span>{formatDuration(job.duration_seconds)}</span>
                                        </div>
                                    {:else}
                                        <span class="text-muted">—</span>
                                    {/if}
                                </td>
                            </tr>
                        {/each}
                    </tbody>
                </table>
            </div>

            {#if paging.last_page > 1}
                <div class="pagination">
                    <span class="pagination-info">
                        Showing {paging.from || 0}–{paging.to || 0} of {paging.total}
                    </span>
                    <div class="pagination-buttons">
                        <button type="button" disabled={currentPage <= 1} on:click={() => goToPage(currentPage - 1)}>Previous</button>
                        <span class="page-indicator">Page {currentPage} of {paging.last_page}</span>
                        <button type="button" disabled={currentPage >= paging.last_page} on:click={() => goToPage(currentPage + 1)}>Next</button>
                    </div>
                </div>
            {/if}
        {/if}
    {/if}
</div>

{#if showDetailModal && selectedJob}
    <div
        class="modal-overlay"
        role="dialog"
        aria-labelledby="job-detail-title"
        aria-modal="true"
    >
        <button type="button" class="modal-backdrop" aria-label="Close dialog" on:click={closeDetailModal}></button>
        <div class="modal-content" role="document">
            <div class="modal-header">
                <h3 id="job-detail-title">AI Process Detail</h3>
                <button type="button" class="modal-close" on:click={closeDetailModal} aria-label="Close">&times;</button>
            </div>
            <div class="modal-body">
                <dl class="detail-list">
                    <dt>Agent</dt>
                    <dd>
                        <div class="type-wrapper">
                            <div class="type-icon" data-subject={selectedJob.subject_type}>
                                <i class="{getSubjectIcon(selectedJob.subject_type)} icon"></i>
                            </div>
                            <span class="type-label">{getSubjectLabel(selectedJob.subject_type)}</span>
                        </div>
                    </dd>

                    <dt>Report</dt>
                    <dd>
                        {#if selectedJob.subject_path}
                            <a use:link href={selectedJob.subject_path} class="modal-item-link">
                                {getItemDisplayName(selectedJob)}
                            </a>
                        {:else}
                            <span class="text-muted">—</span>
                        {/if}
                    </dd>

                    <dt>Status</dt>
                    <dd>
                        <span class="status-badge" style="--badge-color: {getStatusColor(selectedJob.status)}">
                            {#if selectedJob.status === 'processing'}
                                <span class="pulse-dot"></span>
                            {/if}
                            {getStatusLabel(selectedJob.status)}
                        </span>
                    </dd>

                    <dt>User</dt>
                    <dd>
                        {#if selectedJob.user}
                            {selectedJob.user.first_name} {selectedJob.user.last_name} ({selectedJob.user.email})
                        {:else}
                            —
                        {/if}
                    </dd>

                    <dt>Created</dt>
                    <dd>{formatDate(selectedJob.created_at)}</dd>

                    <dt>Started</dt>
                    <dd>{formatDate(selectedJob.started_at)}</dd>

                    <dt>Finished</dt>
                    <dd>{formatDate(selectedJob.finished_at)}</dd>

                    <dt>Duration</dt>
                    <dd>{formatDuration(selectedJob.duration_seconds)}</dd>

                    {#if selectedJob.error_message}
                        <dt>Error</dt>
                        <dd>
                            <div class="error-block">{selectedJob.error_message}</div>
                        </dd>
                    {/if}

                    <dt>Job ID</dt>
                    <dd><code class="uuid-display">{selectedJob.uuid}</code></dd>

                    <dt>Subject ID</dt>
                    <dd><code class="uuid-display">{selectedJob.subject_uuid}</code></dd>
                </dl>
            </div>
            <div class="modal-footer">
                <button type="button" class="modal-close-btn" on:click={closeDetailModal}>Close</button>
            </div>
        </div>
    </div>
{/if}

<style>
    .ai-jobs-page {
        padding: var(--space-5) var(--space-6);
        width: 100%;
    }

    /* Page Header — matches BriefHome */
    .page-header {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        margin-bottom: 2rem;
        gap: 1.5rem;
        flex-wrap: wrap;
    }

    .header-content {
        flex: 1;
        min-width: 200px;
    }

    .page-title {
        font-size: 2rem;
        font-weight: 500;
        color: var(--grey);
        margin: 0 0 0.5rem 0;
    }

    .page-subtitle {
        font-size: 1rem;
        color: var(--grey-secondary);
        margin: 0;
    }

    .active-indicator {
        display: inline-flex;
        align-items: center;
        gap: 0.5rem;
        padding: 0.625rem 1.25rem;
        background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
        border: 2px solid #3b82f6;
        border-radius: 20px;
        font-size: 0.9375rem;
        font-weight: 600;
        color: #1e40af;
    }

    .pulse-dot-header {
        width: 10px;
        height: 10px;
        border-radius: 50%;
        background: #3b82f6;
        animation: pulse 1.5s ease-in-out infinite;
    }

    /* Filter Bar — matches BriefList search bar */
    .search-filter-bar {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: 1.5rem;
        gap: 1rem;
    }

    .filter-controls {
        display: flex;
        gap: 0.5rem;
        align-items: center;
        flex-wrap: wrap;
    }

    .filter-select {
        padding: 0.875rem 1rem;
        border: 2px solid var(--border-color);
        border-radius: 8px;
        font-size: 1rem;
        color: var(--grey);
        background: white;
        transition: all 0.2s ease;
        min-width: 140px;
    }

    .filter-select:focus {
        outline: none;
        border-color: var(--primary);
        box-shadow: 0 0 0 3px rgba(5, 108, 242, 0.1);
    }

    .clear-filters-btn {
        display: inline-flex;
        align-items: center;
        gap: 0.25rem;
        padding: 0.875rem 1rem;
        background: transparent;
        border: 2px solid var(--border-color);
        border-radius: 8px;
        cursor: pointer;
        font-size: 0.9375rem;
        color: var(--grey-secondary);
        transition: all 0.2s ease;
    }

    .clear-filters-btn i { margin: 0; font-size: 0.8rem; }

    .clear-filters-btn:hover {
        background: var(--greyish);
        color: var(--grey);
    }

    .refresh-btn {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 44px;
        height: 44px;
        background: linear-gradient(135deg, #056cf2 0%, #4790ee 100%);
        color: white;
        border: none;
        border-radius: 8px;
        cursor: pointer;
        transition: all 0.2s ease;
    }

    .refresh-btn i { margin: 0; }

    .refresh-btn:hover:not(:disabled) {
        transform: translateY(-1px);
        box-shadow: 0 2px 8px rgba(5, 108, 242, 0.3);
    }

    .refresh-btn:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }

    .results-count {
        font-size: 0.9375rem;
        color: var(--grey-secondary);
        font-weight: 500;
        white-space: nowrap;
    }

    /* Loading State — matches BriefList */
    .loading-state {
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        min-height: 400px;
        padding: 4rem 2rem;
        gap: 1rem;
    }

    .loading-spinner { color: var(--primary); }
    .loading-spinner i.icon { font-size: 3rem; }

    .loading-text {
        margin: 0;
        font-size: 1rem;
        color: var(--grey-secondary);
        font-weight: 400;
    }

    /* Empty State */
    .empty-state {
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        min-height: 400px;
        padding: 4rem 2rem;
        text-align: center;
    }

    .empty-icon-wrap {
        width: 72px;
        height: 72px;
        background: linear-gradient(135deg, #056cf2 0%, #4790ee 100%);
        border-radius: 16px;
        display: flex;
        align-items: center;
        justify-content: center;
        margin-bottom: 1.5rem;
        opacity: 0.6;
    }

    .empty-icon-wrap i { font-size: 2rem; color: white; margin: 0; }

    .empty-title {
        font-size: 1.25rem;
        font-weight: 500;
        color: var(--grey);
        margin: 0 0 0.5rem 0;
    }

    .empty-hint {
        font-size: 0.9375rem;
        color: var(--grey-secondary);
        margin: 0 0 1rem 0;
    }

    /* Zero State (no jobs at all) — richer onboarding view */
    .empty-zero-state {
        display: flex;
        flex-direction: column;
        align-items: center;
        text-align: center;
        padding: 4rem 2rem;
        background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
        border-radius: 16px;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08), 0 2px 6px rgba(0, 0, 0, 0.04);
        border: 1px solid rgba(5, 108, 242, 0.08);
    }

    .zero-icon-wrap {
        width: 88px;
        height: 88px;
        background: linear-gradient(135deg, #056cf2 0%, #4790ee 100%);
        border-radius: 20px;
        display: flex;
        align-items: center;
        justify-content: center;
        margin-bottom: 1.5rem;
        box-shadow: 0 8px 24px rgba(5, 108, 242, 0.25);
    }

    .zero-icon-wrap i {
        font-size: 2.5rem;
        color: white;
        margin: 0;
    }

    .zero-title {
        font-size: 1.5rem;
        font-weight: 500;
        color: var(--grey);
        margin: 0 0 0.5rem 0;
    }

    .zero-hint {
        font-size: 1rem;
        color: var(--grey-secondary);
        margin: 0 0 2rem 0;
        max-width: 520px;
        line-height: 1.5;
    }

    .zero-actions {
        display: flex;
        gap: 1rem;
        flex-wrap: wrap;
        justify-content: center;
        width: 100%;
        max-width: 640px;
    }

    .zero-action-card {
        display: flex;
        align-items: center;
        gap: 0.875rem;
        padding: 1rem 1.25rem;
        background: white;
        border: 2px solid var(--border-color);
        border-radius: 12px;
        text-decoration: none;
        color: var(--grey);
        transition: all 0.2s ease;
        flex: 1 1 240px;
        min-width: 220px;
        text-align: left;
    }

    .zero-action-card:hover {
        border-color: var(--primary);
        transform: translateY(-2px);
        box-shadow: 0 6px 16px rgba(5, 108, 242, 0.15);
    }

    .zero-action-icon {
        width: 44px;
        height: 44px;
        --za-1: #056cf2;
        --za-2: #4790ee;
        background: linear-gradient(135deg, var(--za-1) 0%, var(--za-2) 100%);
        border-radius: 10px;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
    }
    .zero-action-icon--brief {
        --za-1: var(--accent-orange);
        --za-2: color-mix(in srgb, var(--accent-orange) 78%, white);
    }
    .zero-action-icon--gapfinder {
        --za-1: var(--accent-pink);
        --za-2: color-mix(in srgb, var(--accent-pink) 72%, white);
    }

    .zero-action-icon i {
        font-size: 1.25rem;
        color: white;
        margin: 0;
    }

    .zero-action-text {
        display: flex;
        flex-direction: column;
        gap: 0.125rem;
        min-width: 0;
    }

    .zero-action-title {
        font-weight: 500;
        font-size: 0.9375rem;
        color: var(--grey);
    }

    .zero-action-sub {
        font-size: 0.8125rem;
        color: var(--grey-secondary);
    }

    /* Table — matches BriefList */
    .jobs-table-wrapper {
        background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
        border-radius: 16px;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08), 0 2px 6px rgba(0, 0, 0, 0.04);
        overflow: hidden;
        border: 1px solid rgba(5, 108, 242, 0.08);
    }

    .jobs-table {
        width: 100%;
        border-collapse: collapse;
        border-spacing: 0;
        margin: 0;
        table-layout: fixed;
    }

    /* Header — gradient blue, matches BriefList */
    .jobs-table thead {
        background: linear-gradient(135deg, #056cf2 0%, #4790ee 100%);
    }

    .jobs-table thead th {
        padding: 1.25rem 1rem;
        text-align: left;
        font-weight: 500;
        color: white;
        font-size: 0.875rem;
        text-transform: uppercase;
        letter-spacing: 0.05em;
        border-bottom: 3px solid rgba(255, 255, 255, 0.2);
    }

    .jobs-table thead th i:first-child {
        margin-right: 0.5rem;
        opacity: 0.9;
    }

    .th-type { width: 16%; }
    .th-item { width: 28%; }
    .th-status { width: 14%; }
    .th-user { width: 18%; }
    .th-created { width: 14%; }
    .th-duration { width: 10%; }

    .td-item {
        max-width: 0;
    }

    .item-link {
        display: inline-flex;
        align-items: center;
        gap: 0.35rem;
        max-width: 100%;
        color: #056cf2;
        font-weight: 500;
        text-decoration: none;
    }

    .item-link:hover {
        text-decoration: underline;
    }

    .item-link-text {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        min-width: 0;
    }

    .item-link-icon {
        flex-shrink: 0;
        font-size: 0.85em;
        opacity: 0.75;
    }

    .modal-item-link {
        color: #056cf2;
        font-weight: 500;
    }

    /* Rows — matches BriefList */
    .jobs-table tbody tr.job-row {
        border-bottom: 1px solid rgba(5, 108, 242, 0.08);
        transition: all 0.3s ease;
        cursor: pointer;
    }

    .jobs-table tbody tr.job-row:hover {
        background: linear-gradient(90deg, rgba(5, 108, 242, 0.05) 0%, rgba(255, 255, 255, 0) 100%);
        box-shadow: 0 2px 8px rgba(5, 108, 242, 0.1);
    }

    .jobs-table tbody tr.job-row:last-child {
        border-bottom: none;
    }

    .jobs-table tbody td {
        padding: 1.25rem 1rem;
        color: var(--grey);
        vertical-align: middle;
    }

    /* Type column — icon + label like BriefList name column */
    .type-wrapper {
        display: flex;
        align-items: center;
        gap: 0.75rem;
    }

    .type-icon {
        width: 40px;
        height: 40px;
        /* Default (unknown type): app primary blue */
        --type-icon-grad-1: #056cf2;
        --type-icon-grad-2: #4790ee;
        --type-icon-glow: rgba(5, 108, 242, 0.35);
        background: linear-gradient(135deg, var(--type-icon-grad-1) 0%, var(--type-icon-grad-2) 100%);
        border-radius: 10px;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
        transition: all 0.3s ease;
    }

    /* Match dashboard / AssessmentTile agent accents */
    .type-icon[data-subject="brief"] {
        --type-icon-grad-1: var(--accent-orange);
        --type-icon-grad-2: color-mix(in srgb, var(--accent-orange) 78%, white);
        --type-icon-glow: color-mix(in srgb, var(--accent-orange) 45%, transparent);
    }
    .type-icon[data-subject="gapfinder"] {
        --type-icon-grad-1: var(--accent-pink);
        --type-icon-grad-2: color-mix(in srgb, var(--accent-pink) 72%, white);
        --type-icon-glow: color-mix(in srgb, var(--accent-pink) 42%, transparent);
    }
    .type-icon[data-subject="assessment"] {
        --type-icon-grad-1: var(--accent-teal);
        --type-icon-grad-2: color-mix(in srgb, var(--accent-teal) 72%, white);
        --type-icon-glow: color-mix(in srgb, var(--accent-teal) 42%, transparent);
    }

    .jobs-table tbody tr:hover .type-icon {
        transform: scale(1.1) rotate(5deg);
        box-shadow: 0 4px 12px var(--type-icon-glow);
    }

    .type-icon i { font-size: 1.25rem; color: white; margin: 0; }

    .type-label {
        font-weight: 500;
        font-size: 0.9375rem;
        color: var(--grey);
    }

    /* Status badge */
    .status-badge {
        display: inline-flex;
        align-items: center;
        gap: 0.375rem;
        padding: 0.375rem 0.75rem;
        background: color-mix(in srgb, var(--badge-color) 15%, white);
        color: var(--badge-color);
        border: 1.5px solid color-mix(in srgb, var(--badge-color) 35%, transparent);
        border-radius: 20px;
        font-size: 0.8125rem;
        font-weight: 600;
    }

    .pulse-dot {
        width: 8px;
        height: 8px;
        border-radius: 50%;
        background: var(--badge-color);
        animation: pulse 1.5s ease-in-out infinite;
    }

    @keyframes pulse {
        0%, 100% { opacity: 1; transform: scale(1); }
        50% { opacity: 0.5; transform: scale(0.8); }
    }

    .user-name {
        font-weight: 400;
        font-size: 0.9375rem;
    }

    .date-value {
        color: var(--grey);
        font-size: 0.9375rem;
        font-weight: 400;
    }

    .duration-badge {
        display: inline-flex;
        align-items: center;
        padding: 0.375rem 0.75rem;
        background: var(--greyish);
        border: 1.5px solid var(--border-color);
        border-radius: 20px;
        font-size: 0.875rem;
        font-weight: 500;
        font-variant-numeric: tabular-nums;
        color: var(--grey);
    }

    .text-muted {
        color: var(--grey-secondary);
    }

    /* Pagination */
    .pagination {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-top: 1.5rem;
        padding: 1rem 0;
    }

    .pagination-info {
        font-size: 0.9375rem;
        color: var(--grey-secondary);
    }

    .pagination-buttons {
        display: flex;
        gap: 0.75rem;
        align-items: center;
    }

    .pagination-buttons button {
        padding: 0.625rem 1.25rem;
        border: 2px solid var(--border-color);
        background: white;
        border-radius: 8px;
        cursor: pointer;
        font-weight: 500;
        color: var(--grey);
        transition: all 0.2s ease;
    }

    .pagination-buttons button:hover:not(:disabled) {
        border-color: var(--primary);
        color: var(--primary);
    }

    .pagination-buttons button:disabled {
        opacity: 0.4;
        cursor: not-allowed;
    }

    .page-indicator {
        font-size: 0.875rem;
        color: var(--grey-secondary);
    }

    /* Modal */
    .modal-overlay {
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        display: flex;
        justify-content: center;
        align-items: center;
        z-index: 2000;
    }

    .modal-backdrop {
        position: absolute;
        inset: 0;
        border: none;
        padding: 0;
        margin: 0;
        background: var(--shade-background-color);
        cursor: default;
    }

    .modal-content {
        position: relative;
        z-index: 1;
        background: var(--white);
        border-radius: 16px;
        box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
        max-width: 640px;
        width: 92%;
        max-height: 88vh;
        overflow: hidden;
        display: flex;
        flex-direction: column;
    }

    .modal-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 1.25rem 1.5rem;
        border-bottom: 1px solid var(--border-color);
        background: linear-gradient(135deg, #056cf2 0%, #4790ee 100%);
    }

    .modal-header h3 {
        margin: 0;
        font-size: 1.25rem;
        font-weight: 500;
        color: white;
    }

    .modal-close {
        background: rgba(255, 255, 255, 0.2);
        border: none;
        font-size: 1.25rem;
        cursor: pointer;
        color: white;
        width: 32px;
        height: 32px;
        border-radius: 8px;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: background 0.2s ease;
    }

    .modal-close:hover {
        background: rgba(255, 255, 255, 0.3);
    }

    .modal-body {
        overflow-y: auto;
        flex: 1;
        padding: 1.5rem;
    }

    .modal-footer {
        padding: 1rem 1.5rem;
        border-top: 1px solid var(--border-color);
        background: var(--greyish);
        flex-shrink: 0;
    }

    .modal-close-btn {
        display: block;
        width: 100%;
        padding: 0.875rem 1.5rem;
        background: linear-gradient(135deg, #056cf2 0%, #4790ee 100%);
        color: white;
        border: none;
        border-radius: 10px;
        font-size: 1rem;
        font-weight: 500;
        cursor: pointer;
        transition: all 0.2s ease;
    }

    .modal-close-btn:hover {
        transform: translateY(-1px);
        box-shadow: 0 2px 8px rgba(5, 108, 242, 0.3);
    }

    .detail-list {
        margin: 0;
        display: grid;
        grid-template-columns: auto 1fr;
        gap: 0.75rem 1.5rem;
        align-items: center;
    }

    .detail-list dt {
        font-weight: 500;
        color: var(--grey-secondary);
        font-size: 0.875rem;
        text-transform: uppercase;
        letter-spacing: 0.03em;
    }

    .detail-list dd {
        margin: 0;
        font-size: 0.9375rem;
        color: var(--grey);
    }

    .error-block {
        background: #fef2f2;
        color: #dc2626;
        padding: 0.75rem;
        border-radius: 8px;
        font-size: 0.875rem;
        border: 1.5px solid #fecaca;
    }

    .uuid-display {
        font-size: 0.75rem;
        background: var(--greyish);
        padding: 0.25rem 0.5rem;
        border-radius: 6px;
        word-break: break-all;
        font-family: monospace;
    }

    /* Responsive */
    @media (max-width: 768px) {
        .ai-jobs-page {
            padding: var(--space-4);
        }
        .page-header {
            flex-direction: column;
            align-items: stretch;
        }

        .search-filter-bar {
            flex-direction: column;
            align-items: stretch;
        }

        .filter-controls {
            flex-direction: column;
        }

        .filter-select {
            width: 100%;
        }

        .results-count {
            text-align: center;
        }

        .jobs-table-wrapper {
            border-radius: 12px;
            overflow-x: auto;
        }

        .jobs-table thead th,
        .jobs-table tbody td {
            padding: 1rem 0.75rem;
            font-size: 0.8125rem;
        }

        .jobs-table thead th {
            font-size: 0.75rem;
        }

        .jobs-table thead th i { display: none; }

        .type-icon {
            width: 32px;
            height: 32px;
        }

        .type-icon i { font-size: 1rem; }
    }
</style>
